import { FC, ForwardedRef, PropsWithoutRef, forwardRef, useEffect } from 'react';
import { get } from 'lodash';
import { useFormContext, useWatch } from 'react-hook-form';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { EditNew24, ChevronDown16, ChevronUp16, AiColor16, AiColor24 } from 'ui/icons';
import { ALIGNMENT } from 'ui/selects/constants';
import { FrequencyOption } from 'modules/frequencyManagement/components/frequencyModeSelect/types';
import { MessageMedium } from 'constants/messageMediums';
import { colors } from 'ui/theme';
import { CircleSpinner } from 'ui/spinner';
import { MessageType } from 'api/MessageTypeAPI';
import { FrequencyMode } from 'modules/frequencyManagement/lib/types';
import { useFocusContext } from 'modules/frequencyManagement/contexts/FocusContext';
import { LABEL_BY_FREQUENCY_MODE } from 'modules/frequencyManagement/lib/constants';
import { MODE } from 'modules/frequencyManagement/contexts/FormProvider';
import { SelectValueType, SelectValueDisplayProps } from 'ui/selects';
import { lowerCaseMessageMedium } from 'modules/frequencyManagement/lib/utils/mediumDomainUtils';
import {
  ValueWrapper,
  IconWrapper,
  ErrorIcon,
  ValueDisplay,
} from 'ui/selects/SelectValueDisplay.styles';
import { BasicSelectFieldWithFocusHook } from './BasicSelectFieldWithFocusHook';
import { LabelTextId } from './FrequencyModeSelect.constants';
import {
  FlexBaseGap,
  InlineFlexHalfGap,
  Body2LightNoMargin,
  OptimizeSelectedTrigger,
} from './FrequencyModeSelect.styles';

export const createMediumSelectOptions = (
  messageMedium: MessageMedium,
  isOptimizeEnabled = false,
  isAtOptimizeLimit = false,
): FrequencyOption[] => {
  const allOptions = [
    {
      id: `${messageMedium}-off`,
      label: LABEL_BY_FREQUENCY_MODE[FrequencyMode.Off],
      value: FrequencyMode.Off,
    },
    {
      id: `${messageMedium}-fixed-cap`,
      label: LABEL_BY_FREQUENCY_MODE[FrequencyMode.Cap],
      value: FrequencyMode.Cap,
    },
    {
      id: `${messageMedium}-optimize`,
      label: LABEL_BY_FREQUENCY_MODE[FrequencyMode.Optimize],
      value: FrequencyMode.Optimize,
      Suffix: <AiColor16 />,
      isDisabled: isAtOptimizeLimit,
      tooltipContent: isAtOptimizeLimit
        ? 'You already have 10 total optimizations across your project and therefore cannot add another.'
        : null,
    },
  ];
  if (isOptimizeEnabled) {
    return allOptions;
  }
  return allOptions.slice(0, -1);
};

export const createMessageTypeSelectOptions = (
  messageMedium: MessageMedium,
  messageTypeName: MessageType['name'],
  isOptimizeEnabled = false,
  isAtOptimizeLimit = false,
): FrequencyOption[] => {
  const allOptions = [
    {
      id: `${messageMedium}-${messageTypeName}-off`,
      label: `Follow ${lowerCaseMessageMedium(messageMedium)} rule`,
      value: FrequencyMode.Off,
      Suffix: <>Default</>,
    },
    {
      id: `${messageMedium}-${messageTypeName}-no-cap`,
      label: LABEL_BY_FREQUENCY_MODE[FrequencyMode.NoCap],
      value: FrequencyMode.NoCap,
    },
    {
      id: `${messageMedium}-${messageTypeName}-fixed-cap`,
      label: LABEL_BY_FREQUENCY_MODE[FrequencyMode.Cap],
      value: FrequencyMode.Cap,
    },
    {
      id: `${messageMedium}-${messageTypeName}-optimize`,
      label: LABEL_BY_FREQUENCY_MODE[FrequencyMode.Optimize],
      value: FrequencyMode.Optimize,
      Suffix: <AiColor16 />,
      isDisabled: isAtOptimizeLimit,
      tooltipContent: isAtOptimizeLimit
        ? 'You already have 10 total optimizations across your project and therefore cannot add another.'
        : null,
    },
  ];
  if (isOptimizeEnabled) {
    return allOptions;
  }
  return allOptions.slice(0, -1);
};

export const FrequencyModeMultiSelect = forwardRef(function InnerSelectValue<
  TValue extends SelectValueType = string,
>(
  {
    isError = false,
    isReadOnly = false,
    verticalAlignment = ALIGNMENT.DOWN,
    children,
    isLoading = false,
    isDisabled = false,
    placeholder = '',
    isOpen = false,
    isNonInteractive = false,
    selectedOptions = [],
    toggleDropdown,
    ...props
  }: PropsWithoutRef<Omit<SelectValueDisplayProps<TValue>, 'content'>>,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const { label, value } = selectedOptions[0] ?? {};
  const icon = verticalAlignment === ALIGNMENT.DOWN ? <ChevronDown16 /> : <ChevronUp16 />;

  return (
    <ValueWrapper
      ref={ref}
      data-test="basic-select-frequency-mode-trigger"
      aria-disabled={isDisabled}
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      data-values={selectedOptions.map(({ value: selectedValue }) => selectedValue)}
      onClick={
        isNonInteractive
          ? undefined
          : (event) => {
              if (typeof ref !== 'function') {
                ref?.current?.focus(); // force focus for FF/Safari
              }
              event.stopPropagation();
              toggleDropdown();
            }
      }
      {...props}
    >
      {value === FrequencyMode.Optimize && (
        <OptimizeSelectedTrigger>
          <AiColor24 />
        </OptimizeSelectedTrigger>
      )}
      <ValueDisplay>{children || label || placeholder}</ValueDisplay>
      <IconWrapper isReadOnly={isReadOnly}>
        {isError && !isReadOnly && <ErrorIcon />}
        {isLoading ? <CircleSpinner size={16} color={colors.secondaryText} /> : icon}
      </IconWrapper>
    </ValueWrapper>
  );
});

export interface FrequencyModeSelectProps {
  rootName?: string;
  options: FrequencyOption[];
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
}
export const FrequencyModeSelect: FC<FrequencyModeSelectProps> = ({
  rootName,
  options,
  isEditing,
  setIsEditing,
  ...rest
}) => {
  const name = rootName ? `${rootName}.${MODE}` : MODE;
  const {
    formState: { defaultValues },
  } = useFormContext();
  const mode = useWatch({ name }) as FrequencyMode;
  const { setFocusIdForRootName } = useFocusContext();

  const defaultValue = get(defaultValues, name);
  const isEditHidden = defaultValue === FrequencyMode.Off;
  useEffect(() => {
    /**
     * The Edit Button is hidden if mode is set to Off. In this case,
     * we need to ensure that we update the "isEditing" state to true when
     * the user does actually change the mode.
     */
    if (isEditHidden && !isEditing && mode !== defaultValue) {
      setIsEditing(true);
    }
  }, [isEditHidden, isEditing, mode, defaultValue, setIsEditing]);

  return (
    <div>
      {isEditing || isEditHidden ? (
        <BasicSelectFieldWithFocusHook
          Trigger={FrequencyModeMultiSelect}
          name={name}
          options={options}
          horizontalAlignment={ALIGNMENT.RIGHT}
          rootName={rootName}
          {...rest}
        />
      ) : (
        <FlexBaseGap>
          <InlineFlexHalfGap>
            {mode === FrequencyMode.Optimize && <AiColor24 />}
            <Body2LightNoMargin data-test={LabelTextId}>
              {LABEL_BY_FREQUENCY_MODE[mode]}
            </Body2LightNoMargin>
          </InlineFlexHalfGap>
          {!isEditHidden && (
            <IconButton
              size={BUTTON_SIZES.LARGE}
              variant={BUTTON_VARIANTS.SECONDARY}
              onClick={() => {
                setFocusIdForRootName(mode, rootName);
                setIsEditing(true);
              }}
            >
              <EditNew24 />
            </IconButton>
          )}
        </FlexBaseGap>
      )}
    </div>
  );
};
